import React from 'react';
import { ArrowPathIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import HiddenIcon from '../../icons/HiddenIcon';
import { colorForEvent, EVENT_COLOR_CLASSES } from '../eventColors';
import type { CalendarEvent, CalendarEventDetail } from '../../../types/integrations';

interface EventHeaderProps {
  event: CalendarEvent | CalendarEventDetail;
}

const EventHeader: React.FC<EventHeaderProps> = ({ event }) => {
  const color = EVENT_COLOR_CLASSES[colorForEvent(event.id)];
  const isCancelled = event.status === 'cancelled';
  const isRecurring = event.is_recurring;
  const isPrivate = event.visibility === 'private' || event.visibility === 'confidential';
  const isOOO = event.event_type === 'outOfOffice';

  return (
    <div className="flex items-start gap-3">
      <span
        className={['mt-1 w-1.5 h-12 rounded-full shrink-0', color.swatch].join(' ')}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <h2 className="text-xl font-bold text-text-primary leading-tight break-words">
          {event.summary || 'Untitled event'}
        </h2>
        {event.calendar_name && (
          <p className="text-xs text-text-tertiary mt-1 truncate">
            {event.calendar_name}
          </p>
        )}
        {(isCancelled || isRecurring || isPrivate || isOOO) && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {isCancelled && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-error-500/12 text-error-700 dark:text-error-200 text-[11px] font-bold uppercase tracking-wider">
                Canceled
              </span>
            )}
            {isRecurring && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-elevated text-text-secondary text-[11px] font-medium">
                <ArrowPathIcon className="w-3 h-3" /> Recurring
              </span>
            )}
            {isPrivate && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-elevated text-text-secondary text-[11px] font-medium">
                <HiddenIcon className="w-3 h-3" /> Private
              </span>
            )}
            {isOOO && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-warning-500/12 text-warning-700 dark:text-warning-200 text-[11px] font-medium">
                <BriefcaseIcon className="w-3 h-3" /> Out of office
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventHeader;
